<template>
  <div class="row reverse-mobile justify-content-center">
    <UserMenu></UserMenu>

    <div class="col-12 col-md-9 center-content">
      <CommunityCard class="p-4">
        <h3 class="mb-4 text-inherit">Media Upload</h3>

        <p>Share your REALITi content with iSimulate's simulation community.</p>
        <p>
          By default, your media will be unpublished and only visible to you.
        </p>

        <div class="disclaimer-box mb-4">
          <p class="mb-2">
            By uploading media to this portal, you acknowledge that you are solely responsible for the content you share. You must ensure that any uploaded material does not infringe on copyrights, trademarks, or other intellectual property rights. Additionally, content must not be offensive, inappropriate, or violate any laws or community guidelines.
          </p>
          <p class="mb-2">
            <b>
            3B Scientific/iSimulate reserves the right to remove any content deemed inappropriate, offensive, or in violation of these terms, at its sole discretion.
            </b>
          </p>
          <p class="mb-0">
            By proceeding, you confirm that you have the necessary rights and permissions to share the uploaded content.
          </p>
        </div>

        <div v-if="!isOnNative">
          <form enctype="multipart/form-data">
            <vue-dropzone
              ref="dropzone"
              id="drop1"
              :options="dropOptions"
              v-on:vdropzone-file-added="fileDroppedInZone"
              v-on:vdropzone-removed-file="fileRemoved"
              :useCustomSlot="true"
            >
              <p>{{ dropText }}</p>
            </vue-dropzone>
          </form>
        </div>

        <div class="d-flex flew-row" v-if="isOnNative">
          <a class="mr-2">
            <button
              class="btn btn-primary d-flex align-items-center justify-content-center d-inline-block"
              @click="requestNativeMedia"
            >
              <i class="material-icons mr-1">import_export</i>
              Import from REALITi
            </button>
          </a>
          <a v-if="uploadObjects.length > 0" class="mr-2">
            <button
              class="btn btn-primary d-flex align-items-center justify-content-center d-inline-block"
              @click="selectAll"
            >
              <i class="material-icons mr-1">select_all</i>
              Select all
            </button>
          </a>
          <a v-if="uploadObjects.length > 0" class="mr-2">
            <button
              class="btn btn-success d-flex align-items-center justify-content-center d-inline-block"
              @click="uploadSelectedClicked"
            >
              <i class="material-icons mr-1">cloud_upload</i>
              Upload Selected
            </button>
          </a>
        </div>

        <p
          class="text-center pb-2"
          :class="[validationError ? 'bg-danger text-white' : '']"
        >
          <sub>
            Up to 10 files may be uploaded at a time. Maximum 50MB per file.
            <br />
            Media types supported:
            <code :class="[validationError ? 'text-warning' : '']">
              .jpg, .jpeg, .bmp, .gif, .png
            </code>
          </sub>
        </p>

        <div class="container-table-row" v-if="uploadObjects.length > 0">
          <div class="container-table-cell">
            <table class="table table-responsive">
              <thead>
                <tr>
                  <th>ID</th>
                  <th v-if="isOnNative">Upload</th>
                  <th>File</th>
                  <th>Message</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(file, idx) in uploadObjects" v-bind:key="idx">
                  <th scope="row">{{ file.id + 1 }}</th>
                  <td class="text-center" v-if="isOnNative">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="file.toggledForUpload"
                      value
                      id="index"
                    />
                  </td>
                  <td>{{ file.name }}</td>
                  <td class="text-left" v-if="file.hasUploaded">
                    <span class="font-italic">Upload complete</span>
                  </td>
                  <td class="text-left" v-else-if="file.hasErrors">
                    <span class="font-italic">{{ file.errorMessage }}</span>
                  </td>
                  <td v-else></td>
                  <td
                    class="text-center d-flex justify-content-center align-items-center"
                    v-if="file.hasUploaded"
                  >
                    <i class="material-icons mr-1 text-success">check_circle</i>
                  </td>
                  <td
                    class="text-center d-flex justify-content-center align-items-center"
                    v-else-if="file.hasErrors"
                  >
                    <i class="material-icons mr-1 text-danger">error</i>
                  </td>
                  <td
                    class="text-center d-flex justify-content-center align-items-center"
                    v-else-if="
                      !file.hasUploaded &&
                        !file.hasErrors &&
                        file.toggledForUpload &&
                        uploadStarted
                    "
                  >
                    <img
                      class="uploading-file-spinner"
                      src="../assets/uploading-file-spinner.gif"
                      alt="uploading, please wait"
                    />
                  </td>
                  <td
                    class="text-center d-flex justify-content-center align-items-center"
                    v-else
                  ></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </CommunityCard>

      <Footer />
    </div>
  </div>
</template>
<script>
import UserMenu from "../components/UserMenu";
import CommunityCard from "../components/CommunityCard";
import Footer from "../components/Footer";
import mediaService from "../services/media.service";
import MediaToUploadObject from "../objects/MediaToUploadObject";
import logger from "../utils/logger";
import vueDropzone from "vue2-dropzone";

const VALID_EXTENSIONS = ["jpg", "jpeg", "bmp", "gif", "png"];

export default {
  name: "MediaUpload",
  data: function() {
    return {
      dropText: "Drop your media files here to upload to Community.",
      uploadObjects: [],
      uploadStarted: false,
      scenarioPath: "",
      dropOptions: {
        url: "none",
        autoProcessQueue: false,
        maxFilesize: 50, // MB
        maxFiles: 10,
        addRemoveLinks: true,
      },
    };
  },

  computed: {
    validationError() {
      let isValid = true;
      this.uploadObjects.forEach(obj => {
        // extn may not always be set, still can be valid
        const ext = obj.hasOwnProperty("extn") ? obj.extn : undefined;
        const extensionValid =
          ext === undefined ||
          VALID_EXTENSIONS.includes(obj.extn.toLowerCase());

        if (obj.hasErrors || !extensionValid) {
          isValid = false;
        }
      });

      return !isValid || this.uploadObjects.length > 10;
    },
    isOnNative() {
      return this.$store.getters.getIsOnNative;
    },
  },

  components: {
    UserMenu,
    CommunityCard,
    Footer,
    vueDropzone,
  },

  beforeMount() {
    this.registerNativeMethodPointers();
  },

  methods: {
    registerNativeMethodPointers() {
      window.updateAvaiableMedia = this.updateAvaiableMedia;
      window.setMediaPath = this.setMediaPath;
      window.receiveMediaItem = this.receiveMediaItem;
    },

    requestNativeMedia() {
      this.resetUploadState();

      window.webkit.messageHandlers.getMediaPath.postMessage("request");
      window.webkit.messageHandlers.mediaExport.postMessage("request");
    },

    updateAvaiableMedia(fileNames) {
      this.createNativeUploadObjects(JSON.parse(fileNames));
    },

    createNativeUploadObjects(fileNames) {
      for (let [index, fileName] of fileNames.entries()) {
        this.uploadObjects.push(
          new MediaToUploadObject(index, fileName, this.mediaPath)
        );
      }
    },

    uploadSelectedClicked() {
      this.uploadStarted = true;
      this.uploadObjects = this.uploadObjects.filter(function(object) {
        return object.toggledForUpload === true;
      });

      this.uploadObjects.forEach(object => {
        this.processUploadObject(object);
      });
    },

    processUploadObject(object) {
      window.webkit.messageHandlers.getMediaItem.postMessage({
        path: object.path,
        assignedCallBackId: object.id,
      });
    },

    receiveMediaItem(recivedBase64, receivedCallBackId) {
      let recivedObject = this.uploadObjects.find(
        object => object.id === parseInt(receivedCallBackId)
      );

      let blob = this.base64ToBlob(recivedBase64, recivedObject.mimeType);
      let recivedObjectFile = new File([blob], recivedObject.name);
      this.sendOffMediaItem(recivedObjectFile);
    },

    base64ToBlob(base64Data, mimeType) {
      base64Data = base64Data.replace(/^[^,]+,/, "");
      mimeType = mimeType || "";

      let sliceSize = 1024;
      let byteCharacters = atob(base64Data);
      let bytesLength = byteCharacters.length;
      let slicesCount = Math.ceil(bytesLength / sliceSize);
      let byteArrays = new Array(slicesCount);

      for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
        let begin = sliceIndex * sliceSize;
        let end = Math.min(begin + sliceSize, bytesLength);
        let bytes = new Array(end - begin);

        for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
          bytes[i] = byteCharacters[offset].charCodeAt(0);
        }

        byteArrays[sliceIndex] = new Uint8Array(bytes);
      }
      return new Blob(byteArrays, { type: mimeType });
    },

    fileRemoved(file) {
      const idx = this.uploadObjects.findIndex(v => v.name === file.name);
      if (idx !== -1) {
        this.uploadObjects.splice(idx, 1);
      }
    },

    fileDroppedInZone(file) {
      this.uploadStarted = true;

      this.uploadObjects.push(
        new MediaToUploadObject(
          this.uploadObjects.length,
          file.name,
          this.mediaPath,
          true
        )
      );

      this.sendOffMediaItem(file);
    },

    sendOffMediaItem(fileToSend) {
      let formData = new FormData();
      formData.append("mediaFiles", fileToSend);
      this.uploadFormData(formData);
    },

    uploadFormData(formData) {
      mediaService.uploadMedia(formData).then(response => {
        response.errors.forEach(error => {
          this.processUploadError(error);
        });

        response.successes.forEach(success => {
          this.processUploadSuccess(success);
        });
      });
    },

    processUploadError(error) {
      logger.error("media.upload_failed", "Media upload failed", { filename: error.filename, message: error.message });
      this.uploadObjects.forEach(object => {
        if (error.filename === object.name) {
          object.hasErrors = true;
          object.errorMessage = error.message;
        }
      });
    },

    processUploadSuccess(success) {
      logger.log("media.uploaded", "Media uploaded", { filename: success.filename });
      this.uploadObjects.forEach(object => {
        if (success.filename === object.name) {
          object.hasUploaded = true;
        }
      });
    },

    selectAll() {
      this.uploadObjects.forEach(object => {
        object.toggledForUpload = true;
      });
    },

    setMediaPath(path) {
      this.mediaPath = path;
    },

    resetUploadState() {
      this.uploadObjects = [];
      this.uploadStarted = false;
    },
  },
};
</script>

<style scoped>
.disclaimer-box {
  background: #fff8e1;
  border-left: 4px solid #f59e0b;
  border-radius: 4px;
  padding: 16px 20px;
  font-size: 0.88rem;
  color: #5a4a1a;
}

tr {
  min-height: 75px;
}

.uploading-file-spinner {
  height: 25px;
  width: auto;
  -webkit-filter: grayscale(100%); /* Safari 6.0 - 9.0 */
  filter: grayscale(100%);
}
</style>
